import type { YearsResponse, ThemesResponse, PhotosResponse } from '../types';
import { getCached, setCache } from './cache';

const API_BASE = import.meta.env.VITE_API_URL || '';

export class ApiError extends Error {
  status: number;
  constructor(status: number, statusText: string) {
    super(`API error: ${status} ${statusText}`);
    this.status = status;
  }
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new ApiError(response.status, response.statusText);
  }
  return response.json();
}

/**
 * Fetch with in-memory caching. Returns cached data if available,
 * otherwise fetches from the API and stores the result.
 */
async function cachedFetch<T>(cacheKey: string, url: string): Promise<T> {
  const cached = getCached<T>(cacheKey);
  if (cached) return cached;

  const data = await fetchJson<T>(url);
  setCache(cacheKey, data);
  return data;
}

export const galleryApi = {
  getYears: (): Promise<YearsResponse> =>
    cachedFetch('gallery:years', `${API_BASE}/public/gallery/years`),

  getThemes: (year: string): Promise<ThemesResponse> =>
    cachedFetch(`gallery:themes:${year}`, `${API_BASE}/public/gallery/years/${encodeURIComponent(year)}/themes`),

  getPhotos: (themeId: string): Promise<PhotosResponse> =>
    cachedFetch(`gallery:photos:${themeId}`, `${API_BASE}/public/gallery/themes/${encodeURIComponent(themeId)}/photos`),
};
